
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, AlertCircle, Shield, Wifi, WifiOff } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useSimpleAuth } from '@/hooks/useSimpleAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredOperation?: string;
  resourceId?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredOperation,
  resourceId
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showFallback, setShowFallback] = useState(false);
  const [authGracePeriod, setAuthGracePeriod] = useState(true);
  
  // Try to use main auth context first
  let authContext;
  let authError = null;
  
  try {
    authContext = useAuth();
  } catch (error) {
    console.error('🛡️ ProtectedRoute: Error accessing auth context:', error);
    authError = error;
  }
  
  // Fallback to simple auth if main context fails
  const fallbackAuth = useSimpleAuth();
  
  // Use fallback auth if main context failed
  const { user, loading, error, isConnected } = authContext || fallbackAuth;

  // Grace period for authentication initialization (longer on refresh)
  useEffect(() => {
    if (loading) {
      // Longer grace period for authentication to prevent premature redirects
      const graceTimer = setTimeout(() => {
        setAuthGracePeriod(false);
      }, 3000); // 3 seconds grace period
      
      return () => clearTimeout(graceTimer);
    } else {
      setAuthGracePeriod(false);
    }
  }, [loading]);

  // Show fallback option after extended loading
  useEffect(() => {
    if (loading && !authGracePeriod) {
      const timer = setTimeout(() => {
        setShowFallback(true);
      }, 2000); // Additional 2 seconds before showing fallback
      
      return () => clearTimeout(timer);
    }
  }, [loading, authGracePeriod]);

  // Show loading state during grace period or while actually loading
  if (loading || authGracePeriod) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="flex flex-col items-center space-y-4 max-w-md mx-auto p-6">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-8 w-8 animate-spin text-green-500" />
            {isConnected === false && <WifiOff className="h-5 w-5 text-red-500" />}
            {isConnected !== false && <Wifi className="h-5 w-5 text-green-500" />}
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">
              {authGracePeriod ? 'Restoring session...' : 
               isConnected === false ? 'Connection issues detected...' : 'Verifying access...'}
            </p>
            {error && (
              <p className="text-xs text-red-600 mb-2">{error}</p>
            )}
            {showFallback && (
              <Button 
                onClick={() => {
                  // Store current location before redirecting
                  sessionStorage.setItem('auth_return_url', location.pathname + location.search);
                  navigate('/auth');
                }}
                size="sm"
                variant="outline"
              >
                Go to Login
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show connection error with retry option
  if (error && isConnected === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Alert className="max-w-md" variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Connection Error</AlertTitle>
          <AlertDescription className="mt-2">
            Unable to connect to authentication service. Please check your internet connection.
            <div className="mt-4 flex gap-2">
              <Button 
                onClick={() => window.location.reload()}
                size="sm"
              >
                Retry
              </Button>
              <Button 
                onClick={() => {
                  sessionStorage.setItem('auth_return_url', location.pathname + location.search);
                  navigate('/auth');
                }}
                variant="outline"
                size="sm"
              >
                Go to Login
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // If not authenticated, store current location and redirect to auth page
  if (!user) {
    const returnUrl = location.pathname + location.search;
    sessionStorage.setItem('auth_return_url', returnUrl);
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
